import { ErrorBoundary as SentryErrorBoundary, init as InitSentry } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { Suspense, lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { StoreProvider } from 'lib/store'
import { ChakraProvider, extendTheme } from '@chakra-ui/react'

import { Loader } from 'components/ui'
import { ToastContainer } from 'react-toastify'

import './main.css'
import 'react-toastify/dist/ReactToastify.css'
import 'nprogress/nprogress.css'

const ExitPage = lazy(() => import('pages/ExitPage'))
const Dashboard = lazy(() => import('pages/Dashboard'))
const LandingPage = lazy(() => import('pages/LandingPage'))
const DebugPage = lazy(() => import('pages/Debug'))
const SignUpPage = lazy(() => import('pages/Signup'))
const LoginPage = lazy(() => import('pages/Login'))
const WorkshopPage = lazy(() => import('pages/Workshop'))
const ExportPage = lazy(() => import('pages/Export'))
const NotFound = lazy(() => import('pages/404'))
const DiscordCallbackPage = lazy(() => import('pages/DiscordCallback'))
const PhoneCameraPage = lazy(() => import('pages/PhoneCamera'))
const InstantCoffePage = lazy(() => import('pages/InstantCoffee'))

InitSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [new BrowserTracing()],

  environment: import.meta.env.NODE_ENV === 'production' ? 'production' : 'staging',

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
})

const theme = extendTheme({
  initialColorMode: 'dark',
  styles: {
    // this chakra stuff creates more problems than it solves
    global: {
      body: {
        bg: '#111827',
      },
    },
  },
  components: {
    Tabs: {
      baseStyle: {
        _focus: {
          boxShadow: 'rgba(66, 153, 225, 0.6) 0px 0px 0px 1px',
          outline: 'none',
        },
      },
    },
    Button: {
      baseStyle: {
        _focus: {
          boxShadow: 'rgba(66, 153, 225, 0.6) 0px 0px 0px 1px',
          outline: 'none',
        },
      },
    },
  },
  colors: {
    white: {
      main: '#FAFAF8',
    },
    blue: {
      main: '#182F53',
      dark: '#151B36',
      tw_500: '#3B82F6',
    },
    orange: {
      main: '#F57A4D',
      secondary: '#F35D26',
    },
    green: {
      main: '#5CA66C',
      secondary: '#3C824B',
    },
    purple: {
      main: '#5B21B6',
      secondary: '#7C3AED',
    },
    stroke: 'rgba(204, 204, 204, 0.6)',
  },
})

const ErrorView = () => {
  return (
    <div className='flex h-screen flex-col items-center justify-center'>
      <h1 className='text-4xl font-bold text-white'>An error has occurred</h1>
      <p className='text-white'>Please refresh the page</p>
      <p className='m-1 text-white'>
        If it doesn't help please contact us{' '}
        <a
          href='mailto:hi@skechy.io'
          className='rounded border border-white p-1 hover:border-blue-200 hover:text-blue-200'
        >
          hi@skechy.io
        </a>
      </p>
    </div>
  )
}

const App = () => (
  <SentryErrorBoundary showDialog fallback={ErrorView}>
    {/* <ThemeProvider theme={darkTheme}> */}
    <StoreProvider>
      <ChakraProvider theme={theme}>
        <ToastContainer
          position='bottom-right'
          autoClose={2000}
          hideProgressBar
          pauseOnFocusLoss={false}
          className='pointer-events-none flex flex-col items-end pb-6'
        />
        <BrowserRouter>
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path='/' element={<LandingPage />} />
              <Route path='dashboard' element={<Dashboard />} />
              <Route path='login' element={<LoginPage />} />
              <Route path='signup' element={<SignUpPage />} />
              <Route path='debug' element={<DebugPage />} />
              <Route path='/discord/callback/' element={<DiscordCallbackPage />} />
              <Route path='/workshop/:id' element={<WorkshopPage />} />
              <Route path='/workshop/:id/exit' element={<ExitPage />} />
              <Route path=':id' element={<WorkshopPage />} />
              <Route path=':id/exit' element={<ExitPage />} />
              <Route path=':id/export' element={<ExportPage />} />
              <Route path=':id/phone-camera' element={<PhoneCameraPage />} />
              <Route path='*' element={<NotFound />} />
              <Route path='/coffee' element={<InstantCoffePage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ChakraProvider>
    </StoreProvider>
    {/* </ThemeProvider> */}
  </SentryErrorBoundary>
)

export default App
